import React, { createContext, useContext } from 'react';
import { useAuth } from './AuthContext';

// ─────────────────────────────────────────────
// PERMISSIONS MATRIX (from roles document)
// ─────────────────────────────────────────────
const ROLE_PERMISSIONS = {
  gpf_system_admin: {
    projects:  { access: true, view: true, add: true, edit: true, delete: true },
    calendar:  { access: true, view: true, add: true, edit: true, delete: true },
    files:     { access: true, view: true, add: true, edit: true, delete: true },
    notes:     { access: true, view: true, add: true, edit: true, delete: true },
    reports:   { access: true, view: true },
    users:     { access: true, view: true, add: true, edit: true, delete: true },
  },
  gpf_administrator: {
    projects:  { access: true, view: true, add: true, edit: true, delete: true },
    calendar:  { access: true, view: true, add: true, edit: true, delete: true },
    files:     { access: true, view: true, add: true, edit: true, delete: true },
    notes:     { access: true, view: true, add: true, edit: true, delete: true },
    reports:   { access: true, view: true },
    users:     { access: true, view: true, add: true, edit: true, delete: true },
  },
  gpf_project_manager: {
    projects:  { access: true, view: true, add: true, edit: true, delete: true },
    calendar:  { access: true, view: true, add: true, edit: true, delete: true },
    files:     { access: true, view: true, add: true, edit: true, delete: true },
    notes:     { access: true, view: true, add: true, edit: true, delete: true },
    reports:   { access: true, view: true },
    users:     { access: false, view: false, add: false, edit: false, delete: false },
  },
  gpf_project_user: {
    projects:  { access: true, view: true, add: false, edit: false, delete: false },
    calendar:  { access: true, view: true, add: false, edit: false, delete: false },
    files:     { access: true, view: true, add: true, edit: true, delete: true },
    notes:     { access: true, view: true, add: true, edit: true, delete: false },
    reports:   { access: false, view: false },
    users:     { access: false, view: false, add: false, edit: false, delete: false },
  },
  gpf_client: {
    projects:  { access: true, view: true, add: false, edit: false, delete: false },
    calendar:  { access: false, view: false, add: false, edit: false, delete: false },
    files:     { access: true, view: true, add: true, edit: true, delete: true },
    notes:     { access: true, view: true, add: true, edit: true, delete: false },
    reports:   { access: false, view: false },
    users:     { access: false, view: false, add: false, edit: false, delete: false },
  },
};

// Default — deny everything
const DENY_ALL = {
  projects:  { access: false, view: false, add: false, edit: false, delete: false },
  calendar:  { access: false, view: false, add: false, edit: false, delete: false },
  files:     { access: false, view: false, add: false, edit: false, delete: false },
  notes:     { access: false, view: false, add: false, edit: false, delete: false },
  reports:   { access: false, view: false },
  users:     { access: false, view: false, add: false, edit: false, delete: false },
};

const defaultCan = () => true; // Default: allow everything until context loads
const PermissionsContext = createContext({ can: defaultCan, perms: {}, role: '' });

export function PermissionsProvider({ children }) {
  const { user } = useAuth();
  // Read role from stored user — falls back to localStorage directly
  const storedUser = JSON.parse(localStorage.getItem('gpf_user') || '{}');
  const role = user?.role || storedUser?.role || '';
  const perms    = ROLE_PERMISSIONS[role] || ROLE_PERMISSIONS['gpf_administrator'];

  // Ensure can is always a function

  const can = (module, action) => {
    return perms?.[module]?.[action] === true;
  };

  return (
    <PermissionsContext.Provider value={{ perms, can, role }}>
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions() {
  return useContext(PermissionsContext);
}